import { ColumnsType } from "antd/es/table";
import moment from "moment";
import React, { useContext, useEffect, useRef, useState } from "react";
import { DateRangePicker } from "rsuite";
import { PaginationInput } from "src/api/types";
import {
  adminManagementFilterOptions,
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { useDateTimePicker } from "src/lib/hooks/useDateTimePicker";
import {
  capitalizeEachFirstLetter,
  currentTimeForDataPicker,
  getErrorMessage,
} from "src/lib/utils";
import { Dropdown, MenuProps, Space, Spin, Table } from "antd";
import { useMutation } from "react-query";
import client from "../../api";
import GlobalContext from "src/context/global-context";
import SearchFilter from "src/components/search-filters/reset2fa-filter";

const AdminActivity = () => {
  const { canView, canList } = usePermissions(
    permissionConstants.MANAGE_ADMIN
  );
  const canViewAdminActivity = canView || canList;

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const [filter, setFilter] = useState("user_id");

  const handleStates = () => {
    setSearchText("");
    // setFilter("level");
    // setFilterValue("");
    setStartDate(null);
    setEndDate(null);
    changeDateRangeHandler(null);
  };

  const onDateSelect = (_startDate: string | null, _endDate: string | null) => {
    setStartDate(_startDate);
    setEndDate(_endDate);
  };

  const { afterToday } = DateRangePicker;
  const { dateRangeValue, changeDateRangeHandler } = useDateTimePicker({
    onDateSelect,
  });

  const now = new Date();
  const prevMonth = new Date(now.setMonth(now.getMonth() - 1));

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    containerRef.current && setContainerWidth(containerRef.current.offsetWidth);
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({
      ...searchParams,
      current_page: page,
    });
  };

  const { mutate: getData, isLoading } = useMutation(client.admin.all, {
    onSuccess: (data) => {
      setTotal(data.total);
      setSearchParams((prev: any) => ({
        ...prev,
        per_page: data.per_page,
      }));
      setDataSource(
        data.data.map((item: any) => ({
          key: item.id,
          id: item.id,
          full_name:
            !item?.admin_details?.first_name && !item?.admin_details?.last_name
              ? "__"
              : (item?.admin_details?.first_name ?? " ") +
                " " +
                (item?.admin_details?.last_name ?? " "),
          email: item?.admin_details?.email,
          userId: item?.user_id,
          createdAt: item?.created_at ?? "__",
          updatedAt: item?.updated_at ?? "__",
          changes: item?.changes,
          model: item?.model_type,
        })),
      );
    },
    onError: (error: any) => {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
      }
      setIsAlertOpened(true);
    },
  });

  const columns: ColumnsType<any> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          User Id
        </p>
      ),
      dataIndex: "userId",
      className: "w-auto",
      key: "userId",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <a className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline">
            {value}
          </a>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Name
        </p>
      ),
      dataIndex: "full_name",
      className: "w-auto",
      key: "full_name",
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[14px] font-medium">
            {capitalizeEachFirstLetter(value)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          E-mail
        </p>
      ),
      dataIndex: "email",
      className: "w-auto",
      key: "email",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Model
        </p>
      ),
      dataIndex: "model",
      className: "w-auto",
      key: "model",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[14px] font-medium">
            {value?.split("\\")[3]
              ? value?.split("\\")[3]
              : value?.split("\\")[2]}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Changes
        </p>
      ),
      dataIndex: "changes",
      key: "changes",
      className: "w-auto",
      render: (value: Record<string, string | number>) => (
        <div className="font-manrope text-primary text-[14px] font-normal">
          {Object.entries(value).map(([key, val]) => (
            <p key={key}>
              {key}: {val}
            </p>
          ))}
        </div>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created At
        </p>
      ),
      dataIndex: "createdAt",
      className: "w-auto",
      key: "createdAt",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-normal">
            {value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "__"}
          </p>
        </div>
      ),
    },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Updated At
    //     </p>
    //   ),
    //   dataIndex: "updatedAt",
    //   className: "w-auto",
    //   key: "country",
    //   render: (value) => (
    //     <div className="flex flex-row gap-2 items-center">
    //       <p className="font-manrope text-primary text-[14px] font-normal">
    //         {value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "__"}
    //       </p>
    //     </div>
    //   ),
    // },
  ];

  useEffect(() => {
    if (canViewAdminActivity) {
      getData({
        ...searchParams,
        search_field: filter || undefined,
        search_value: searchText || undefined,
        start_date: startDate,
        end_date: endDate,
      });
    }
  }, [canViewAdminActivity, searchParams.current_page, startDate, endDate, searchText]);

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full h-full flex-col">
        <div className="flex flex-col w-full p-2 sm:p-4">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
            Admin Management
          </p>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex align-center gap-2">
            <div className="">
              <SearchFilter
                options={adminManagementFilterOptions}
                placeholder="Search by UserId,Model"
                value={filter}
                onChange={setFilter}
                searchText={searchText}
                setSearchText={setSearchText}
              />
            </div>
            <DateRangePicker
              format="dd/MM/yyyy"
              size="lg"
              block
              placement="bottomEnd"
              style={{ width: "308px" }}
              value={dateRangeValue}
              disabledDate={afterToday?.()}
              onChange={(range) => {
                setSearchParams({ ...searchParams, current_page: 1 });
                if (range) {
                  const currentTime: Date[] = currentTimeForDataPicker(range);
                  changeDateRangeHandler([currentTime[0], currentTime[1]]);
                } else {
                  changeDateRangeHandler(null);
                }
              }}
              defaultCalendarValue={[prevMonth, now]}
              ranges={[]}
            />
            {/* 
                <SearchFilter
                  options={userManagementFilterOptions}
                  value={filter}
                  onChange={setFilter}
                  filterValue={filterValue}
                  setFilterValue={setFilterValue}
                /> */}
          </div>
        </div>

        {!canViewAdminActivity ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-4 text-center shadow-sm">
            <p className="font-manrope text-[16px] font-semibold text-secondary">
              You do not have permission to view admin activity.
            </p>
          </div>
        ) : (
          <div
            className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4"
            ref={containerRef}
          >
            <Table
              loading={isLoading}
              showSorterTooltip={false}
              columns={columns}
              dataSource={dataSource}
              pagination={{
                total,
                current: searchParams.current_page,
                onChange: onPageChange,
                position: ["bottomCenter"],
                defaultPageSize: LIST_PAGE_SIZE,
                showSizeChanger: false,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminActivity;
